import { useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { icon } from './site-icon';
import { track } from '../../utils/analytics';

type WaitlistState = 'idle' | 'sending' | 'done' | 'invalid' | 'failed';

/** Early-access email capture; posts to the waitlist Worker (waitlist-worker/, D1). */
export function WaitlistForm({ source, currentLang }: { source: string; currentLang: string }) {
  const { t } = useTranslation();
  const id = useId();
  const [state, setState] = useState<WaitlistState>('idle');

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const email = String(form.get('email') ?? '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setState('invalid'); return; }
    setState('sending');
    try {
      const response = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, locale: currentLang, source, website: String(form.get('website') ?? '') })
      });
      setState(response.ok ? 'done' : response.status === 400 ? 'invalid' : 'failed');
      // Mark as a key event in GA4: the signup is the goal of the public site, and
      // D1 alone cannot tell which channel, campaign or page produced it.
      if (response.ok) track('waitlist_signup', { source, locale: currentLang });
    } catch {
      setState('failed');
    }
  };

  if (state === 'done') {
    return <p className="waitlist-done" role="status">{icon('CircleCheck')}{t('site.waitlist.success')}</p>;
  }
  const error = state === 'invalid' ? t('site.waitlist.errorEmail') : state === 'failed' ? t('site.waitlist.errorGeneric') : null;
  return (
    <form className="waitlist" onSubmit={submit} noValidate>
      <label className="sr-only" htmlFor={`${id}-email`}>{t('site.waitlist.label')}</label>
      <div className="waitlist-row">
        <input
          id={`${id}-email`} name="email" type="email" autoComplete="email" inputMode="email" required
          placeholder={t('site.waitlist.placeholder')} aria-invalid={state === 'invalid' || undefined}
          aria-describedby={error ? `${id}-error` : `${id}-note`}
          onInput={() => { if (state === 'invalid' || state === 'failed') setState('idle'); }}
        />
        {/* Honeypot: people never see or fill it. */}
        <input className="waitlist-trap" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" />
        <button type="submit" className="button primary" disabled={state === 'sending'}>
          {t(state === 'sending' ? 'site.waitlist.submitting' : 'site.waitlist.submit')}{state !== 'sending' && icon('ArrowRight')}
        </button>
      </div>
      {error
        ? <p className="waitlist-error" id={`${id}-error`} role="alert">{error}</p>
        : <p className="waitlist-note" id={`${id}-note`}>{t('site.waitlist.consent')} <Link to={`/${currentLang}/privacy-policy`}>{t('site.waitlist.privacy')}</Link></p>}
    </form>
  );
}
